import { NotDisclosed } from "@platform/openapi-types";

import type { ValueFormatterParams } from "ag-grid-community";

/**
 * Commission type rows whose "Unknown" (notDisclosed) value Build shows as "-".
 * Their shared definitions count notDisclosed as missing, so Build renders it like
 * any other empty required cell. Quotes/Present keep the "Unknown" label.
 */
const COMMISSION_TYPE_KEYS: ReadonlySet<string> = new Set([
  "currentCommissionType",
  "requestedCommissionType",
]);

type ValueFormatter = (params: ValueFormatterParams) => string;

interface CommissionTypeRow {
  benefitKey: string;
  benefit: { cell: { valueFormatter: ValueFormatter } };
}

const hasValueFormatter = (
  benefit: unknown,
): benefit is CommissionTypeRow["benefit"] =>
  typeof benefit === "object" &&
  benefit !== null &&
  "cell" in benefit &&
  typeof benefit.cell === "object" &&
  benefit.cell !== null &&
  "valueFormatter" in benefit.cell &&
  typeof benefit.cell.valueFormatter === "function";

const isCommissionTypeRow = (row: unknown): row is CommissionTypeRow => {
  if (
    typeof row !== "object" ||
    row === null ||
    !("benefitKey" in row) ||
    !("benefit" in row)
  ) {
    return false;
  }

  const { benefitKey, benefit } = row;

  return (
    typeof benefitKey === "string" &&
    COMMISSION_TYPE_KEYS.has(benefitKey) &&
    hasValueFormatter(benefit)
  );
};

/**
 * Replaces the commission type rows' cell formatter with one that shows "-" for
 * notDisclosed. Copies each row it changes, so the shared definitions keep their
 * formatter for the other surfaces.
 */
export const hyphenateUnknownCommissions = <TRow>(rows: TRow[]): TRow[] =>
  rows.map((row) => {
    if (!isCommissionTypeRow(row)) {
      return row;
    }

    const { benefit } = row;
    const format = benefit.cell.valueFormatter;

    return {
      ...row,
      benefit: {
        ...benefit,
        cell: {
          ...benefit.cell,
          valueFormatter: (params: ValueFormatterParams): string =>
            params?.value === NotDisclosed ? "-" : format(params),
        },
      },
    };
  });
